import {Alert, Button} from 'platform/components';
import {Box, HStack, Show, Space, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {KeyboardEventHandler, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {emailHtmlToPlainText} from '../../../utils/emailHtmlToPlainText';
import {AiComposerProposal} from '../AiComposerDraft';
import {getChangedParagraphs} from '../getChangedParagraphs';
import {prepareProposedBody} from '../prepareProposedBody';
import type {AiMessageComposerController} from '../useAiMessageComposer';
import {AiComposerChangedText} from './AiComposerChangedText';
import {AiComposerContextList} from './AiComposerContextList';
import {AiComposerFocusRegion} from './AiComposerFocusRegion';
import {AiComposerPill} from './AiComposerPill';
import {AiComposerSourcesButton} from './AiComposerSourcesButton';

const PREVIEW_MAX_HEIGHT = 60;
const CHANGE_BAR = '2px solid';

interface AiComposerProposalBlockProps extends RequiredTestIdProps {
  controller: AiMessageComposerController;
  proposal: AiComposerProposal;
  onKeyDownCapture: KeyboardEventHandler<HTMLDivElement>;
}

const toPlainText = (body: string, isHtml: boolean) => (isHtml ? emailHtmlToPlainText(body) : body);

/**
 * The proposal before it touches the message: its text at the size of the editor, the decision,
 * and the facts behind an info button. A rewrite of the whole text marks its changed paragraphs;
 * the word diff is one click away (Patrik, 2026-10-06).
 */
export function AiComposerProposalBlock(props: AiComposerProposalBlockProps) {
  const [isShowingChanges, setIsShowingChanges] = useState(false);
  const controller = props.controller;
  const proposal = props.proposal;
  const isHtml = controller.isHtmlBody;
  const proposedBody = prepareProposedBody(
    proposal.draft.body,
    isHtml,
    isNotNil(proposal.selection)
  );
  const proposedText = toPlainText(proposedBody, isHtml);
  const isRewrite =
    proposal.operation === 'refine' && isNil(proposal.selection) && controller.hasExistingBody;
  const paragraphs = isRewrite
    ? getChangedParagraphs(toPlainText(proposal.identity.body, isHtml), proposedText)
    : [];

  return (
    <AiComposerFocusRegion
      role="group"
      aria-label={i18n.t('entity.documentTemplateEditor.labels.aiProposalPreview')}
    >
      <VStack spacing={2}>
        <Show when={proposal.isPlaceholderMismatch}>
          <Alert
            variant="warning"
            message={i18n.t('general.aiMessageComposer.labels.placeholderMismatch')}
            data-testid={suffixTestId('placeholderMismatch', props)}
          />
        </Show>

        <Show when={controller.hasSubject && controller.isProposedSubjectApplied}>
          <div data-testid={suffixTestId('proposedSubject', props)}>
            <Text inline size="small" color="secondary">
              {`${i18n.t('general.labels.subject')}: `}
            </Text>
            <Text inline size="small" alternative overflowWrap="anywhere">
              {proposal.draft.subject}
            </Text>
          </div>
        </Show>

        <Box maxHeight={PREVIEW_MAX_HEIGHT} overflowY="auto">
          <Show when={!isRewrite}>
            <Text
              size="small"
              overflowWrap="anywhere"
              preWrap
              data-testid={suffixTestId('proposedBody', props)}
            >
              {proposedText}
            </Text>
          </Show>
          <Show when={isRewrite && isShowingChanges}>
            <AiComposerChangedText
              previousBody={proposal.identity.body}
              currentBody={proposedBody}
              isHtml={isHtml}
              data-testid={suffixTestId('proposed', props)}
            />
          </Show>
          <Show when={isRewrite && !isShowingChanges}>
            <VStack spacing={3} data-testid={suffixTestId('proposedParagraphs', props)}>
              {paragraphs.map((paragraph) => (
                <Paragraph
                  key={paragraph.key}
                  $isChanged={paragraph.kind === 'changed'}
                  data-changed={paragraph.kind === 'changed' || undefined}
                >
                  <Text
                    size="small"
                    overflowWrap="anywhere"
                    strikethrough={paragraph.kind === 'removed'}
                    color={paragraph.kind === 'removed' ? 'tertiary' : 'primary'}
                  >
                    {paragraph.text}
                  </Text>
                </Paragraph>
              ))}
            </VStack>
          </Show>
        </Box>

        <HStack spacing={2} align="center" wrap>
          <Button
            variant="primary"
            size="small"
            title={i18n.t('general.actions.replace')}
            wrap
            isDisabled={!controller.canApplyReplace}
            onClick={() => controller.onApply('replace')}
            data-testid={suffixTestId('replace', props)}
          />
          <Show when={controller.isInsertBelowOffered}>
            <AiComposerPill
              title={i18n.t('general.aiMessageComposer.actions.insertBelow')}
              isDisabled={!controller.canApplyInsertBelow}
              onClick={() => controller.onApply('insertBelow')}
              data-testid={suffixTestId('insertBelow', props)}
            />
          </Show>
          <Button
            variant="ghostLink"
            size="small"
            title={i18n.t('general.actions.discard')}
            wrap
            onClick={controller.onDiscard}
            data-testid={suffixTestId('discard', props)}
          />
          <Show when={isRewrite}>
            <Button
              variant="ghostLink"
              size="small"
              title={
                isShowingChanges
                  ? i18n.t('general.aiMessageComposer.actions.hideChanges')
                  : i18n.t('general.aiMessageComposer.actions.showChanges')
              }
              wrap
              onClick={() => setIsShowingChanges(!isShowingChanges)}
              data-testid={suffixTestId('showChanges', props)}
            />
          </Show>
          <Space fillAvailable />
          <AiComposerSourcesButton
            isOpen={controller.activeSources === 'proposal'}
            onOpenChange={(isOpen) => controller.onSourcesChange(isOpen ? 'proposal' : null)}
            onKeyDownCapture={props.onKeyDownCapture}
            data-testid={suffixTestId('sources', props)}
          >
            <AiComposerContextList
              usedContext={proposal.usedContext}
              unavailableContext={proposal.unavailableContext}
              languageSource={proposal.languageSource}
              locale={proposal.locale}
              title={i18n.t('general.labels.source')}
              data-testid={props['data-testid']}
            />
          </AiComposerSourcesButton>
        </HStack>

        <Show
          when={
            controller.isInsertBelowOffered &&
            controller.canApplyReplace &&
            !controller.canApplyInsertBelow
          }
        >
          <Text
            size="xxSmall"
            color="tertiary"
            overflowWrap="anywhere"
            data-testid={suffixTestId('insertBelowBlocked', props)}
          >
            {i18n.t('general.aiMessageComposer.labels.insertBelowWouldRepeatPlaceholders')}
          </Text>
        </Show>
      </VStack>
    </AiComposerFocusRegion>
  );
}

// A changed paragraph carries a bar, not only a colour, so the mark survives colour blindness.
const Paragraph = styled.div<{$isChanged: boolean}>`
  padding-left: ${({theme}) => theme.getSize(2)};
  border-left: ${CHANGE_BAR}
    ${({theme, $isChanged}) => ($isChanged ? theme.colors.palettes.purple[60][100] : 'transparent')};
`;
